import { useTranslation } from '@/lib/i18n';
import { StatusBadge } from '@/components/ui';
import { formatChargeAmount } from '../lib/chargeMoney';
import { formatDateTime } from './OrdersTable';

/**
 * QA-625 상품별 환불(결제 관리 상품 환불) — BE order #143 이 관리자 주문 상세 items[]·상품주문번호별 lines[] 에 내린다.
 * generated 타입 반영 전이라 안전 캐스팅으로 읽는다. 환불이 없으면(itemRefundStatus null) 아무것도 그리지 않는다.
 */
interface ItemRefundFields {
  itemRefundStatus?: 'REFUNDED' | 'PARTIALLY_REFUNDED' | null;
  refundedQuantity?: number | null;
  /** 청구통화 major 문자열(KRW "5000", USD "11.87"). */
  refundedAmount?: string | null;
  refundedCurrencyCode?: string | null;
  lastRefundedAt?: string | null;
}

export const ItemRefundNote = ({ item, testId }: { item: unknown; testId?: string }) => {
  const { t } = useTranslation('orders');
  const r = (item ?? {}) as ItemRefundFields;
  if (r.itemRefundStatus !== 'REFUNDED' && r.itemRefundStatus !== 'PARTIALLY_REFUNDED') return null;
  const details = [
    r.refundedQuantity ? t('orders.itemRefund.quantity', { count: r.refundedQuantity, defaultValue: '{{count}}개' }) : null,
    r.refundedAmount && r.refundedCurrencyCode ? formatChargeAmount(r.refundedAmount, r.refundedCurrencyCode) : null,
    r.lastRefundedAt ? formatDateTime(r.lastRefundedAt) : null,
  ].filter(Boolean);
  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-[color:var(--content-muted)]" data-testid={testId}>
      <StatusBadge
        size="sm"
        variant="refunded"
        label={
          r.itemRefundStatus === 'REFUNDED'
            ? t('orders.itemRefund.REFUNDED', '환불완료')
            : t('orders.itemRefund.PARTIALLY_REFUNDED', '부분환불')
        }
      />
      {details.length > 0 ? <span className="tabular-nums">{details.join(' · ')}</span> : null}
    </span>
  );
};

/** 상품 중 하나라도 상품 환불이 있으면 true — 처리상태 칸을 열어야 하는지 볼 때 쓴다. */
export const hasItemRefund = (item: unknown): boolean => {
  const status = ((item ?? {}) as ItemRefundFields).itemRefundStatus;
  return status === 'REFUNDED' || status === 'PARTIALLY_REFUNDED';
};
